import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X } from 'phosphor-react';
import { theme } from '../../styles/theme';

interface WellnessPassSheetProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: ${theme.zIndex.modal};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Sheet = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--color-bg);
  animation: ${slideUp} 0.3s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  background: var(--color-bg);
  border-bottom: 1px solid var(--color-bg-elevated);
  flex-shrink: 0;
`;

const HeaderTitle = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  border: none;
  color: var(--color-text);
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const IframeContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: var(--color-bg);
`;

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: ${spinAnimation} 0.8s linear infinite;
`;

export const WellnessPassSheet: React.FC<WellnessPassSheetProps> = ({ isOpen, onClose, url, title = 'WellnessPass' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Reset loading state when URL changes or sheet opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
    }
  }, [isOpen, url]);

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <Sheet
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wellnesspass-sheet-title"
        tabIndex={-1}
      >
        <Header>
          <HeaderTitle id="wellnesspass-sheet-title">{title}</HeaderTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={20} weight="bold" />
          </CloseButton>
        </Header>
        <IframeContainer>
          {isLoading && (
            <LoadingOverlay>
              <Spinner />
            </LoadingOverlay>
          )}
          <StyledIframe
            src={url}
            title={title}
            onLoad={() => setIsLoading(false)}
            allow="geolocation"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </IframeContainer>
      </Sheet>
    </Overlay>
  );
};
